// What the mailbox says after an action, and what Undo on that notice does (busy-my-web#303,
// Figma 40010468-188844 — archive, moved to trash, mark as read, mark as unread).
//
// Every one of these notices used to be a string buried in a store action, each with its own
// plural and its own "already" line, and none could be taken back. Now the wording is data: one
// line for the thing done, one for "there was nothing to do", one for the failure, and the
// inverse action Undo runs — a fresh call to the inverse ENDPOINT, not the inverse store action,
// because the store's calculate* helpers only see rows still on the page and the rows an archive
// just removed are exactly the ones Undo has to reach.

export const UNDO_LABEL = 'Undo'
// A notice that offers Undo has to stay long enough to be taken up.
export const UNDO_AUTOCLOSE = 6000

const one = (single, many) => (n) => (Number(n) === 1 ? single : many(Number(n)))

// The frames' own voice (busy-my-web#319, 40010468-188844 / 40010486-207023 / -208171 / -210692):
// what the mailbox acts on is a CONVERSATION, the line is a sentence and it ends in a full stop —
// "Conversation archived.", "Conversation moved to trash.", "Conversation Marked as read." The
// plurals follow the same sentence, because one notice cannot be two voices.
export const ACTIONS = {
  archive: {
    done: one('Conversation archived.', (n) => `${n} conversations archived.`),
    already: one('Conversation already archived.', (n) => `${n} conversations already archived.`),
    failed: 'Failed to change emails folder',
    undone: 'Archive undone — back in the inbox',
    endpoint: 'timeline/archive',
    undo: 'unarchive'
  },
  unarchive: {
    done: one('Conversation moved to inbox.', (n) => `${n} conversations moved to inbox.`),
    already: one('Conversation already in inbox.', (n) => `${n} conversations already in inbox.`),
    failed: 'Failed to change emails folder',
    undone: 'Moved back to Archived',
    endpoint: 'timeline/unArchive',
    undo: 'archive'
  },
  trash: {
    done: one('Conversation moved to trash.', (n) => `${n} conversations moved to trash.`),
    already: one('Conversation already in trash.', (n) => `${n} conversations already in trash.`),
    failed: 'Failed to delete emails',
    undone: 'Restored from trash',
    endpoint: 'timeline/moveToBin',
    undo: 'restore'
  },
  restore: {
    done: one('Conversation moved to inbox.', (n) => `${n} conversations moved to inbox.`),
    already: one('Conversation already in inbox.', (n) => `${n} conversations already in inbox.`),
    failed: 'Failed to change emails folder',
    undone: 'Moved back to Trash',
    endpoint: 'timeline/moveToInbox',
    undo: 'trash'
  },
  read: {
    done: one('Conversation Marked as read.', (n) => `${n} conversations Marked as read.`),
    already: one('Conversation already read.', (n) => `${n} conversations already read.`),
    failed: 'Failed to change emails flags',
    undone: 'Marked as unread again',
    endpoint: 'timeline/markAsRead',
    undo: 'unread'
  },
  unread: {
    done: one('Conversation Marked as unread.', (n) => `${n} conversations Marked as unread.`),
    already: one('Conversation already unread.', (n) => `${n} conversations already unread.`),
    failed: 'Failed to change emails flags',
    undone: 'Marked as read again',
    endpoint: 'timeline/markAsUnRead',
    undo: 'read'
  },
  // Gone for good has no way back, so it offers none.
  erase: {
    done: one('Conversation deleted forever.', (n) => `${n} conversations deleted forever.`),
    already: one('Conversation already deleted.', (n) => `${n} conversations already deleted.`),
    failed: 'Failed to erase emails',
    undone: '',
    endpoint: 'timeline/erase',
    undo: null
  }
}

export const KINDS = Object.keys(ACTIONS)

const action = (kind) => ACTIONS[kind] || null

export const noticeText = (kind, count) => action(kind)?.done(count) ?? ''
export const alreadyText = (kind, count) => action(kind)?.already(count) ?? ''
export const failedText = (kind) => action(kind)?.failed ?? 'Something went wrong'
export const undoneText = (kind) => action(kind)?.undone ?? ''
export const undoKindOf = (kind) => action(kind)?.undo ?? null
export const endpointFor = (kind) => action(kind)?.endpoint ?? null
export const canUndo = (kind) => !!undoKindOf(kind)

// The toast-update payload for a finished action: the line, and — when the action can be taken
// back — the Undo callback and the longer stay that goes with it. A kind with no way back keeps
// the ordinary auto-close.
export const undoableNotice = (kind, text, undo) => {
  if (!canUndo(kind) || typeof undo !== 'function') return { render: text, autoClose: true }
  return { render: text, autoClose: UNDO_AUTOCLOSE, undo, undoLabel: UNDO_LABEL }
}

export default noticeText
